// src/pages/Master/Master.jsx
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Grid, CardActionArea, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlusSquare,
  faEdit,
  faIdCard,
  faBuilding,
  faPercentage,
  faChartPie,
  faRandom,
  faTag
} from '@fortawesome/free-solid-svg-icons';
import { usePermissions } from '../../context/PermissionContext';
import BackButton from '../../components/BackButton';

// Define the Master Management options
const masterOptions = [
  {
    title: 'Create Pincode',
    icon: faPlusSquare,
    path: '/pincode/create',
    perm: 'master.pincodeCreate',
    description: 'Add new pincodes to the system'
  },
  {
    title: 'Remove Pincodes',
    icon: faEdit,
    path: '/pincode/delete',
    perm: 'master.pincodeRemove',
    description: 'Delete existing pincodes'
  },
  {
    title: 'Manage Designation',
    icon: faIdCard,
    path: '/designations',
    perm: 'master.designations',
    description: 'Manage user roles and designations'
  },
  {
    title: 'Manage Designation Privilege',
    icon: faBuilding,
    path: '/designation-control',
    perm: 'master.designationPrivileges',
    description: 'Control access for different designations'
  },
  {
    title: 'Set GST',
    icon: faPercentage,
    path: '/set-gst',
    perm: 'master.gst',
    description: 'Configure GST percentages'
  },
  {
    title: 'Set Commission %',
    icon: faChartPie,
    path: '/set-all-div',
    perm: 'master.commissionPercent',
    description: 'Manage system divisions'
  },
  {
    title: 'Set CASHBACK SEQUENCE',
    icon: faRandom,
    path: '/set-random',
    perm: 'master.cashbackSequence',
    description: 'Configure random settings'
  },
  {
    title: 'Set Advertisement Price',
    icon: faTag,
    path: '/set-Ad-price',
    perm: 'master.adPrice',
    description: 'Set pricing for advertisement'
  }
];

const Master = () => {
  const { can, loading } = usePermissions();
  const navigate = useNavigate();

  const handleCardClick = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <CircularProgress />
      </Grid>
    );
  }

  // Check if user has access to any of the cards
  const hasAnyAccess = masterOptions.some(option => can(option.perm));

  if (!hasAnyAccess) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <Typography variant="h6" color="error">
          You don't have permission to access any master settings. Please contact your administrator.
        </Typography>
      </Grid>
    );
  }

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br/><br/>
      <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center', mb: 4,mt:-7 }}>
        Master Settings
      </Typography>
      
      <Grid container spacing={3} justifyContent="center" alignItems="stretch" sx={{ padding: 2 }}>
        {masterOptions.map((option) => (
          can(option.perm) && (
            <Grid item xs={12} sm={6} md={4} lg={3} key={option.title} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Card
                elevation={3}
                sx={{
                  width: 250,
                  height: 260,
                  backgroundColor: 'white',
                  transition: 'all 0.3s ease-in-out',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                    '& .MuiTypography-root': { color: 'white' },
                    '& .fa-icon': { color: 'white' },
                  },
                }}
              >
                <CardActionArea 
                  onClick={() => handleCardClick(option.path)}
                  sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 2 }}
                >
                  <CardContent sx={{ textAlign: 'center', padding: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                    <FontAwesomeIcon
                      icon={option.icon}
                      className="fa-icon"
                      color="#FFA500"
                      style={{ fontSize: '4.5rem', marginBottom: '12px' }}
                    />
                    <Typography 
                      gutterBottom 
                      variant="h6" 
                      component="div" 
                      sx={{ fontWeight: 600, minHeight: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', lineHeight: 1.2, fontSize: '1rem' }}
                    >
                      {option.title}
                    </Typography>
                    <Typography 
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5, fontSize: '0.78rem', minHeight: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}
                    >
                      {option.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          )
        ))}
      </Grid>
    </Box>
  );
};

export default Master;